<template>
  <div class="flex w-full gap-8 pb-6">
    <div class="w-1/4 space-y-1">
      <h3 class="font-semibold text-lg text-dark-neutral-500">Specify the questions</h3>
      <p class="text-sm text-gray-500 font-normal">
        Define each question you want to extract from the record.
      </p>
    </div>
    <div class="w-full">
      <div>
        <div class="w-full">
          <div class="w-full border rounded-lg border-mid-neutral-50">
            <div class="w-full text-sm py-7.5">
              <div class="pl-14 px-5">
                <div
                  class="px-3 grid grid-cols-12 gap-6 text-neutral-text-title font-medium space-y-2.5"
                >
                  <div class="col-span-3">Question ID</div>
                  <div class="col-span-5">Question</div>
                  <div class="col-span-3">Extraction type</div>
                  <div class="col-span-1"></div>
                </div>

                <hr class="w-full border-mid-neutral-50" />
              </div>

              <Sortable
                :items="sortedAttributes"
                @item:moved="({ fractionalIndex, item }) => moveAttribute(item, fractionalIndex)"
              >
                <template #item="{ item, index, onDragEnd, onDragStart }">
                  <div class="flex w-full items-center group px-3 hover:bg-light-neutral-300">
                    <div
                      :class="{
                        'invisible group-hover:visible': !review.isPlanReadonly.value,
                        invisible: review.isPlanReadonly.value,
                      }"
                    >
                      <PlanLockGuard>
                        <FloatingMenu
                          :offset="[10, -14]"
                          :arrow="false"
                          :placement="'bottom-start'"
                        >
                          <template #activator="{ show }">
                            <button
                              class="py-1 text-secondary hover:brightness-125 text-mid-neutral-500"
                              :disabled="review.isPlanReadonly.value"
                              @click.stop="show"
                            >
                              <PlusIcon class="size-5" />
                            </button>
                          </template>
                          <template #content="{ hide }">
                            <div class="w-[200px]">
                              <button
                                class="flex gap-2 px-5 py-3 items-center w-full hover:bg-slate-100"
                                @click.stop="
                                  () => {
                                    addAttributesStructure(item, 'qualitative');
                                    hide();
                                  }
                                "
                              >
                                <span class="text-sm font-medium leading-5 text-dark-neutral-500">
                                  Qualitative
                                </span>
                              </button>
                              <button
                                class="flex gap-2 px-5 py-3 items-center w-full text-slate-300 cursor-not-allowed"
                                disabled
                                title="Coming soon"
                              >
                                <span class="text-sm font-medium leading-5"> Quantitative </span>
                              </button>
                              <button
                                v-if="isCuttingEdge"
                                class="flex gap-2 px-5 py-3 items-center w-full"
                                :class="
                                  hasMedicalAlternativesAttribute
                                    ? 'text-slate-300 cursor-not-allowed'
                                    : 'hover:bg-slate-100'
                                "
                                :disabled="hasMedicalAlternativesAttribute"
                                :title="
                                  hasMedicalAlternativesAttribute
                                    ? 'Medical Alternatives attribute already added'
                                    : ''
                                "
                                @click.stop="
                                  () => {
                                    if (!hasMedicalAlternativesAttribute) {
                                      addAttributesStructure(item, 'medicalAlternatives');
                                      hide();
                                    }
                                  }
                                "
                              >
                                <span
                                  class="text-sm font-medium leading-5"
                                  :class="
                                    hasMedicalAlternativesAttribute ? '' : 'text-dark-neutral-500'
                                  "
                                >
                                  Medical Alternatives
                                </span>
                              </button>
                              <button
                                v-if="isCuttingEdge"
                                class="flex gap-2 px-5 py-3 items-center w-full"
                                :class="
                                  hasStudyDesignAttribute
                                    ? 'text-slate-300 cursor-not-allowed'
                                    : 'hover:bg-slate-100'
                                "
                                :disabled="hasStudyDesignAttribute"
                                :title="
                                  hasStudyDesignAttribute
                                    ? 'Study Design attribute already added'
                                    : ''
                                "
                                @click.stop="
                                  () => {
                                    if (!hasStudyDesignAttribute) {
                                      addAttributesStructure(item, 'studyDesign');
                                      hide();
                                    }
                                  }
                                "
                              >
                                <span
                                  class="text-sm font-medium leading-5"
                                  :class="hasStudyDesignAttribute ? '' : 'text-dark-neutral-500'"
                                >
                                  Study Design
                                </span>
                              </button>
                            </div>
                          </template>
                        </FloatingMenu>
                      </PlanLockGuard>
                    </div>

                    <SortableItem
                      :drag-handle-classes="
                        review.isPlanReadonly.value ? 'invisible' : 'invisible group-hover:visible'
                      "
                      class="w-full"
                      @dragstart="onDragStart"
                      @dragend="onDragEnd"
                    >
                      <AttributeStructureBuilder
                        :key="item.id"
                        :structure="item"
                        :question-id="index + 1"
                        @delete-attribute-structure="deleteAttributesStructure"
                      />
                    </SortableItem>
                  </div>
                </template>
              </Sortable>

              <div
                v-if="review.entity.value.plan!.synthesisPlan!.attributesStructure.length === 0"
                class="pl-14"
              >
                <PlanLockGuard>
                  <FloatingMenu :offset="[10, -14]" :arrow="false" :placement="'bottom-start'">
                    <template #activator="{ show }">
                      <button
                        class="w-full max-w-sm text-sm font-medium flex items-center text-secondary-500 py-3 gap-1"
                        :disabled="review.isPlanReadonly.value"
                        @click.stop="show"
                      >
                        <PlusIcon class="size-4" /> Add question
                      </button>
                    </template>
                    <template #content="{ hide }">
                      <div class="w-[200px]">
                        <button
                          class="flex gap-2 px-5 py-3 items-center w-full hover:bg-slate-100"
                          @click.stop="
                            () => {
                              addAttributesStructure(undefined, 'qualitative');
                              hide();
                            }
                          "
                        >
                          <span class="text-sm font-medium leading-5 text-dark-neutral-500">
                            Qualitative
                          </span>
                        </button>
                        <button
                          class="flex gap-2 px-5 py-3 items-center w-full text-slate-300 cursor-not-allowed"
                          disabled
                          title="Coming soon"
                        >
                          <span class="text-sm font-medium leading-5"> Quantitative </span>
                        </button>
                        <button
                          class="flex gap-2 px-5 py-3 items-center w-full"
                          :class="
                            hasMedicalAlternativesAttribute
                              ? 'text-slate-300 cursor-not-allowed'
                              : 'hover:bg-slate-100'
                          "
                          :disabled="hasMedicalAlternativesAttribute"
                          :title="
                            hasMedicalAlternativesAttribute
                              ? 'Medical Alternatives attribute already exists'
                              : ''
                          "
                          @click.stop="
                            () => {
                              if (!hasMedicalAlternativesAttribute) {
                                addAttributesStructure(undefined, 'medicalAlternatives');
                                hide();
                              }
                            }
                          "
                        >
                          <span
                            class="text-sm font-medium leading-5"
                            :class="hasMedicalAlternativesAttribute ? '' : 'text-dark-neutral-500'"
                          >
                            Medical Alternatives
                          </span>
                        </button>
                        <button
                          class="flex gap-2 px-5 py-3 items-center w-full"
                          :class="
                            hasStudyDesignAttribute
                              ? 'text-slate-300 cursor-not-allowed'
                              : 'hover:bg-slate-100'
                          "
                          :disabled="hasStudyDesignAttribute"
                          :title="
                            hasStudyDesignAttribute ? 'Study Design attribute already exists' : ''
                          "
                          @click.stop="
                            () => {
                              if (!hasStudyDesignAttribute) {
                                addAttributesStructure(undefined, 'studyDesign');
                                hide();
                              }
                            }
                          "
                        >
                          <span
                            class="text-sm font-medium leading-5"
                            :class="hasStudyDesignAttribute ? '' : 'text-dark-neutral-500'"
                          >
                            Study Design
                          </span>
                        </button>
                      </div>
                    </template>
                  </FloatingMenu>
                </PlanLockGuard>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import AttributeStructureBuilder from "./AttributeStructureBuilder.vue";
import { injectStrict } from "@app/utils/injectStrict";
import { ReviewKey } from "../../../use-review";
import useLoading from "@app/composables/use-loading";
import useSnackbar from "@app/composables/use-snackbar";
import { SnackbarState } from "@app/types";
import { HttpException } from "@core/exceptions/http.exception";
import { errorMessage } from "@app/utils/error-message";
import Sortable from "@app/components/Global/Sortable/Sortable.vue";
import { AttributeStructure } from "@core/domain/models/data-extraction-plan-attribute";
import { computed } from "vue";
import { generateKeyBetween } from "fractional-indexing";
import { PlusIcon } from "@heroicons/vue/24/outline";
import SortableItem from "@app/components/Global/Sortable/SortableItem.vue";
import PlanLockGuard from "../../PlanLockGuard.vue";
import FloatingMenu from "@app/components/Global/FloatingMenu.vue";
import { DevToolboxKey } from "@app/injectionKeys";
const { isCuttingEdge } = injectStrict(DevToolboxKey);
const review = injectStrict(ReviewKey);
const loading = useLoading();
const snackbar = useSnackbar();
const sortedAttributes = computed(() =>
  review.entity.value.plan!.synthesisPlan!.attributesStructure.toSorted((a, b) => {
    if (a.order === null && b.order === null) return 0;
    if (a.order === null) return -1;
    if (b.order === null) return 1;
    return a.order > b.order ? 1 : -1;
  }),
);

const hasMedicalAlternativesAttribute = computed(() =>
  review.entity.value.plan!.synthesisPlan!.attributesStructure.some(
    (attr) => attr.type === "medicalAlternatives",
  ),
);

const hasStudyDesignAttribute = computed(() =>
  review.entity.value.plan!.synthesisPlan!.attributesStructure.some(
    (attr) => attr.type === "studyDesign",
  ),
);

async function deleteAttributesStructure(attributeStructureId: string) {
  try {
    loading.start();
    await review.deleteAttributeStructure(review.entity.value.id, attributeStructureId);
    snackbar.success("Success");
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  } finally {
    loading.stop();
  }
}

async function addAttributesStructure(
  afterItem?: AttributeStructure,
  type: string = "qualitative",
) {
  try {
    loading.start();
    let order = "a0";

    if (afterItem) {
      const currentIndex = sortedAttributes.value.findIndex((attr) => attr.id === afterItem.id);
      const nextItem = sortedAttributes.value[currentIndex + 1];
      order = generateKeyBetween(afterItem.order, nextItem?.order ?? null);
    } else {
      const lastAttribute = sortedAttributes.value.at(-1);
      if (lastAttribute) {
        order = generateKeyBetween(lastAttribute.order, null);
      }
    }

    await review.addAttributeStructure(review.entity.value.id, {
      label: "",
      question: "",
      order,
      type: type as any,
    });

    snackbar.success("Success");
  } catch (e) {
    const error = e as HttpException;

    snackbar.show(SnackbarState.ERROR, "An error occured during adding attribute structure");
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  } finally {
    loading.stop();
  }
}

async function moveAttribute(attribute: AttributeStructure, order: string) {
  loading.start();
  try {
    await review.editAttributeStructure(review.entity.value.id, {
      ...attribute,
      order,
    });
  } finally {
    loading.stop();
  }
}
</script>
